import React, { useRef, useState, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useGLTF, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import axios from 'axios';

// Add API_URL constant (ensure it matches the one used in App.js)
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function BathroomScene({
  stickerMode,
  setStickerPosition,
  stickerPosition,
  userUploadedStickerImage,
  onPlacingChange,
  lightMode,
  pendingStickers = [], // Add pendingStickers prop with default empty array
}) {
  // Create a reference for the bathroom stall
  const stall = useRef();
  const pointLightRef = useRef();

  // Blue-tinted dim lighting
  const blueLight = '#1a1aff';
  const ambientIntensity = 0.2;
  const directionalIntensity = 0.2;

  // For raycasting
  const { camera, gl } = useThree();
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  const [intersection, setIntersection] = useState(null);
  const [placedStickers, setPlacedStickers] = useState([]);
  // Store the last loaded stickers to prevent unnecessary reloads
  const lastLoadedStickers = useRef([]);
  const [previewSticker, setPreviewSticker] = useState(null);
  const [stickerImage, setStickerImage] = useState(null);

  // Use a ref for texture cache instead of state to prevent re-renders
  const customStickerTexturesRef = useRef({});

  // Track texture loading status
  const texturesLoaded = useRef(false);

  const [isPlacing, setIsPlacing] = useState(false);
  const [frozenIntersection, setFrozenIntersection] = useState(null);

  // For randomized rotation and scale
  const [randomRotationAngle, setRandomRotationAngle] = useState(0);
  const [randomScale, setRandomScale] = useState(1);
  const [frozenRotationAngle, setFrozenRotationAngle] = useState(0);
  const [frozenScale, setFrozenScale] = useState(1);

  // For throttling position updates
  const lastMousePosition = useRef(new THREE.Vector2());
  const mouseHasMoved = useRef(false);
  const lastUpdateTime = useRef(0);

  // Sticker size
  const stickerSize = 0.09; // Base size for stickers
  const [stickersAspectRatio, setStickersAspectRatio] = useState(1); // Default to square

  // Load floor texture
  const floorTexture = useTexture('/floor.jpg');

  // Make the texture repeat
  floorTexture.wrapS = floorTexture.wrapT = THREE.RepeatWrapping;
  floorTexture.repeat.set(16, 16); // Adjust the repeat values as needed

  // Add new state variables for tracking the cursor position and camera rotation at the top of the component
  const cursorPosition = useRef(new THREE.Vector2(0, 0));
  const frozenCursorPosition = useRef(new THREE.Vector2(0, 0)); // Store cursor position when placing
  const targetCameraRotation = useRef(new THREE.Euler(-0.5, 0, 0)); // Default camera rotation
  const currentCameraRotation = useRef(new THREE.Euler(-0.5, 0, 0));
  // Add refs to track the transition state between modes
  const transitionFactor = useRef(0); // 0 = normal mode, 1 = sticker mode
  const transitionStartTime = useRef(0); // When the transition started
  const transitionDirection = useRef(0); // 1 = to sticker mode, -1 = to normal mode, 0 = no transition
  const transitionDuration = 1.0; // 1 second transition

  // Add camera transition when exiting placement mode
  const exitPlacementTransition = useRef({
    active: false,
    startTime: 0,
    duration: 1.0, // 1 second transition
    startPosition: new THREE.Vector2(0, 0),
    progress: 0,
  });

  // Add touch tracking for mobile support
  const isTouching = useRef(false);
  const lastTouchPosition = useRef(new THREE.Vector2());
  const touchVelocity = useRef(new THREE.Vector2());
  const touchInertia = useRef(new THREE.Vector2());
  const inertiaDecay = 0.95; // How quickly inertia fades
  const touchSensitivity = 2.5; // Increase sensitivity for touch

  // Add variable to track if user is actively dragging to change camera view during placement
  const isActivelyRotating = useRef(false);
  const activeTouchTime = useRef(0);
  const touchDragThreshold = 0.03; // Threshold to detect intentional camera rotation

  // Flag to control when sticker rendering should be forced
  const shouldUpdateStickers = useRef(false);

  // Reset the isPlacing state when stickerMode is toggled
  useEffect(() => {
    if (!stickerMode) {
      setIsPlacing(false);
      setFrozenIntersection(null);
    }
  }, [stickerMode]);

  // Reset the isPlacing state when stickerPosition is reset from parent
  useEffect(() => {
    if (!stickerPosition && isPlacing) {
      // Start transition from frozen position to normal cursor control
      exitPlacementTransition.current.active = true;
      exitPlacementTransition.current.startTime = Date.now();
      exitPlacementTransition.current.startPosition.copy(frozenCursorPosition.current);

      setIsPlacing(false);
      setFrozenIntersection(null);
    }
  }, [stickerPosition, isPlacing]);

  // Handle sticker mode and light intensity transitions
  useEffect(() => {
    if (stickerMode) {
      // If there's a custom sticker image, use that
      if (userUploadedStickerImage) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.src = e.target.result;
          img.onload = () => {
            // Calculate aspect ratio of the image
            const aspectRatio = img.width / img.height;
            setStickersAspectRatio(aspectRatio);

            const texture = new THREE.Texture(img);
            texture.needsUpdate = true;
            texture.premultiplyAlpha = false; // Fix transparency handling
            setStickerImage(texture);
          };
        };
        reader.readAsDataURL(userUploadedStickerImage);
      }
    } else {
      setIntersection(null);
      setPreviewSticker(null);
    }
  }, [stickerMode, userUploadedStickerImage]);

  // Load stickers from server instead of localStorage
  useEffect(() => {
    const loadStickers = async () => {
      try {
        // Fetch stickers from the server
        const response = await axios.get(`${API_URL}/stickers/`);
        const stickers = response.data.stickers || [];

        // Check if stickers have actually changed to avoid unnecessary re-renders
        if (JSON.stringify(lastLoadedStickers.current) === JSON.stringify(stickers)) {
          // Stickers haven't changed, no need to update state
          return;
        }

        // Update the stickers and store for comparison
        lastLoadedStickers.current = stickers;
        setPlacedStickers(stickers);

        // Load textures for stickers immediately - optimized version
        stickers.forEach((sticker, index) => {
          // Check if we have an imageFile or imageData URL
          if (sticker.imageFile || sticker.imageData) {
            // Determine the image URL
            let imageUrl;

            if (sticker.imageData && sticker.imageData.startsWith('http'))
              imageUrl = sticker.imageData;
            else {
              imageUrl = sticker.imageFile
                ? `${API_URL}/stickers/${sticker.imageFile}`
                : sticker.imageData;
            }

            // Create and load the texture immediately
            const img = new Image();
            img.src = imageUrl;
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
              if (!customStickerTexturesRef.current[index]) {
                const texture = new THREE.Texture(img);
                texture.needsUpdate = true;
                texture.premultiplyAlpha = false;
                customStickerTexturesRef.current[index] = texture;
                shouldUpdateStickers.current = true; // Force re-render
                // Force immediate re-render if texture wasn't available initially
                if (texturesLoaded.current) {
                  setPlacedStickers([...stickers]); // Trick to trigger re-render
                }
              }
            };
          }
        });

        // Mark textures as loaded
        texturesLoaded.current = true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading stickers from server:', error);
      }
    };

    // Load stickers immediately on component mount
    loadStickers();

    // Listen for sticker updates via custom event
    document.addEventListener('stickersUpdated', loadStickers);

    return () => {
      document.removeEventListener('stickersUpdated', loadStickers);
    };
  }, []);

  // Force reload stickers when a new one is confirmed
  useEffect(() => {
    if (!stickerMode && !stickerPosition) {
      // Reload stickers from server
      document.dispatchEvent(new Event('stickersUpdated'));
    }
  }, [stickerMode, stickerPosition]);

  // Update the handleMouseMove function to track cursor position for camera rotation
  const handleMouseMove = (event) => {
    // Calculate mouse position in normalized device coordinates
    const newMouseX = (event.clientX / gl.domElement.clientWidth) * 2 - 1;
    const newMouseY = -(event.clientY / gl.domElement.clientHeight) * 2 + 1;

    // Store current cursor position for camera rotation
    cursorPosition.current.set(newMouseX, newMouseY);

    // Only proceed with sticker-specific behavior when in sticker mode
    if (!stickerMode) return;

    // Always update the mouse position for raycasting
    mouse.x = newMouseX;
    mouse.y = newMouseY;

    // Check if mouse has actually moved significantly (for performance)
    if (
      Math.abs(newMouseX - lastMousePosition.current.x) > 0.001 ||
      Math.abs(newMouseY - lastMousePosition.current.y) > 0.001
    ) {
      // Store the current position
      lastMousePosition.current.set(newMouseX, newMouseY);

      // Flag that mouse has moved significantly for raycasting
      mouseHasMoved.current = true;

      // Only update random values if not in placing mode
      if (!isPlacing) {
        // Randomize rotation angle around the normal vector and scale slightly
        setRandomRotationAngle(Math.random() * Math.PI * 2); // 0 to 2π (full rotation)
        setRandomScale(0.9 + Math.random() * 0.2); // Scale: 0.9 to 1.1
      }
    }
  };

  // Handle touch events for mobile camera rotation
  const handleTouchStart = (event) => {
    if (event.touches.length === 1) {
      // Stop propagation to prevent .app-container from intercepting touch events in sticker mode
      if (stickerMode) {
        event.stopPropagation();
      }

      isTouching.current = true;

      const touch = event.touches[0];
      const newTouchX = (touch.clientX / gl.domElement.clientWidth) * 2 - 1;
      const newTouchY = -(touch.clientY / gl.domElement.clientHeight) * 2 + 1;

      lastTouchPosition.current.set(newTouchX, newTouchY);
      touchVelocity.current.set(0, 0);
      touchInertia.current.set(0, 0);

      // If in sticker mode, update raycasting position
      if (stickerMode) {
        mouse.x = newTouchX;
        mouse.y = newTouchY;
        mouseHasMoved.current = true;
      }
    }
  };

  const handleTouchMove = (event) => {
    if (event.touches.length === 1 && isTouching.current) {
      // Always prevent scrolling behavior
      event.preventDefault();

      // Stop propagation to prevent .app-container from intercepting touch events in sticker mode
      if (stickerMode) {
        event.stopPropagation();
      }

      const touch = event.touches[0];
      const newTouchX = (touch.clientX / gl.domElement.clientWidth) * 2 - 1;
      const newTouchY = -(touch.clientY / gl.domElement.clientHeight) * 2 + 1;

      // Calculate velocity (how fast the touch is moving)
      touchVelocity.current.x = (newTouchX - lastTouchPosition.current.x) * touchSensitivity;
      touchVelocity.current.y = (newTouchY - lastTouchPosition.current.y) * touchSensitivity;

      // Detect if user is actively trying to rotate the camera
      const touchMoveMagnitude = Math.sqrt(
        Math.pow(touchVelocity.current.x, 2) + Math.pow(touchVelocity.current.y, 2)
      );

      // If movement exceeds threshold, consider it an active rotation attempt
      if (touchMoveMagnitude > touchDragThreshold) {
        isActivelyRotating.current = true;
        activeTouchTime.current = Date.now();
      }

      // Set inertia based on velocity
      touchInertia.current.copy(touchVelocity.current);

      // Update cursor position for camera rotation
      // Always update cursor position, even during placement mode
      cursorPosition.current.x += touchVelocity.current.x;
      cursorPosition.current.y += touchVelocity.current.y;

      // Clamp cursor position to reasonable bounds
      cursorPosition.current.x = Math.max(-1.5, Math.min(1.5, cursorPosition.current.x));
      cursorPosition.current.y = Math.max(-1.5, Math.min(1.5, cursorPosition.current.y));

      // If in sticker mode, update raycasting position
      if (stickerMode) {
        mouse.x = newTouchX;
        mouse.y = newTouchY;
        mouseHasMoved.current = true;

        // Only update random values if not in placing mode
        if (!isPlacing) {
          setRandomRotationAngle(Math.random() * Math.PI * 2);
          setRandomScale(0.9 + Math.random() * 0.2);
        }
      }

      lastTouchPosition.current.set(newTouchX, newTouchY);
    }
  };

  const handleTouchEnd = (event) => {
    // Stop propagation to prevent .app-container from intercepting touch events in sticker mode
    if (stickerMode) {
      event.stopPropagation();
    }

    isTouching.current = false;

    // Reset the active rotation flag
    isActivelyRotating.current = false;

    // Only process tap if it wasn't a drag (check velocity is small)
    const isSmallMovement =
      Math.abs(touchVelocity.current.x) < 0.02 && Math.abs(touchVelocity.current.y) < 0.02;

    // If in sticker mode, we check if this was a tap to place
    if (stickerMode && isSmallMovement && !isPlacing && intersection && userUploadedStickerImage) {
      // Store current cursor position before modifying anything
      const savedCursorPosition = cursorPosition.current.clone();

      // Smoothly set cursor position - this keeps camera from jerking
      // Add small deliberate delay to ensure smooth transition
      setTimeout(() => {
        // Same logic as handleClick but with smoother transition
        setFrozenIntersection(intersection);
        setFrozenRotationAngle(randomRotationAngle);
        setFrozenScale(randomScale);

        // Don't immediately copy current position - use saved position
        frozenCursorPosition.current.copy(savedCursorPosition);
        setIsPlacing(true);

        const offsetPoint = intersection.point
          .clone()
          .add(intersection.normal.clone().multiplyScalar(0.055));

        // Reset touch velocity and inertia to prevent camera drift after tap
        touchVelocity.current.set(0, 0);
        touchInertia.current.set(0, 0);

        setStickerPosition({
          point: offsetPoint,
          normal: intersection.normal.clone(),
          rotationAngle: randomRotationAngle,
          scale: randomScale,
          placementTime: Date.now(),
        });
      }, 10); // Minimal delay for smoother transition
    }
  };

  // Perform raycasting if mouse has moved and enough time has passed
  const performRaycasting = () => {
    if (!stickerMode || !stall.current) return;

    const currentTime = Date.now();

    // Only perform raycasting if mouse has moved and it's been at least 100ms since last update
    if (mouseHasMoved.current && currentTime - lastUpdateTime.current >= 100) {
      // Update the raycaster
      raycaster.setFromCamera(mouse, camera);

      // Find intersections with the bathroom stall
      const intersects = raycaster.intersectObject(stall.current, true);

      if (intersects.length > 0) {
        const hit = intersects[0];
        setIntersection({
          point: hit.point.clone(),
          normal: hit.face.normal.clone(),
        });
      } else {
        setIntersection(null);
      }

      // Reset the moved flag and update the last update time
      mouseHasMoved.current = false;
      lastUpdateTime.current = currentTime;
    }
  };

  // Handle click to place sticker - only allow when actively placing
  const handleClick = (event) => {
    if (!stickerMode || !userUploadedStickerImage) return;

    if (isPlacing) {
      // If already placing, ignore clicks
      return;
    }

    if (!intersection) return;

    // Freeze the current intersection, rotation angle, and scale
    setFrozenIntersection(intersection);
    setFrozenRotationAngle(randomRotationAngle);
    setFrozenScale(randomScale);

    // Freeze cursor position for stable camera view during placement
    frozenCursorPosition.current.copy(cursorPosition.current);

    setIsPlacing(true);

    // Include the offset in the saved position data
    // Add a slightly larger base offset to ensure stickers stand off from the wall enough
    const offsetPoint = intersection.point
      .clone()
      .add(intersection.normal.clone().multiplyScalar(0.055));

    // Save the modified intersection with offset
    setStickerPosition({
      point: offsetPoint,
      normal: intersection.normal.clone(),
      rotationAngle: randomRotationAngle,
      scale: randomScale,
      placementTime: Date.now(),
    });
  };

  // Setup event listeners and raycasting interval
  useEffect(() => {
    // Initialize the last mouse position
    lastMousePosition.current.set(0, 0);
    mouseHasMoved.current = false;
    lastUpdateTime.current = 0;

    // Get the DOM element for event listeners
    const domElement = gl.domElement;

    // Add capture phase event listeners for touch events to ensure they run before app-container
    const touchStartOptions = { passive: false, capture: true };
    const touchMoveOptions = { passive: false, capture: true };
    const touchEndOptions = { passive: true, capture: true };

    // Always add mouse and touch event listeners for tracking cursor position
    domElement.addEventListener('mousemove', handleMouseMove);
    domElement.addEventListener('touchstart', handleTouchStart, touchStartOptions);
    domElement.addEventListener('touchmove', handleTouchMove, touchMoveOptions);
    domElement.addEventListener('touchend', handleTouchEnd, touchEndOptions);

    if (stickerMode) {
      // Set up interval for checking if we need to perform raycasting
      // Reduced frequency to prevent frame drops
      const raycastCheckInterval = setInterval(performRaycasting, 100);

      // Add click listener for placing stickers
      window.addEventListener('click', handleClick);

      return () => {
        window.removeEventListener('click', handleClick);
        clearInterval(raycastCheckInterval);
        domElement.removeEventListener('mousemove', handleMouseMove);
        domElement.removeEventListener('touchstart', handleTouchStart, touchStartOptions);
        domElement.removeEventListener('touchmove', handleTouchMove, touchMoveOptions);
        domElement.removeEventListener('touchend', handleTouchEnd, touchEndOptions);
      };
    }

    return () => {
      domElement.removeEventListener('mousemove', handleMouseMove);
      domElement.removeEventListener('touchstart', handleTouchStart, touchStartOptions);
      domElement.removeEventListener('touchmove', handleTouchMove, touchMoveOptions);
      domElement.removeEventListener('touchend', handleTouchEnd, touchEndOptions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickerMode, intersection]);

  // Update the useFrame function to add more natural movement to the camera
  useFrame((state) => {
    const t = state.clock.getElapsedTime();

    // Apply inertia when not touching
    if (
      !isTouching.current &&
      (Math.abs(touchInertia.current.x) > 0.001 || Math.abs(touchInertia.current.y) > 0.001)
    ) {
      // Apply inertia to cursor position
      cursorPosition.current.x += touchInertia.current.x;
      cursorPosition.current.y += touchInertia.current.y;

      // Clamp cursor position to reasonable bounds
      cursorPosition.current.x = Math.max(-1.5, Math.min(1.5, cursorPosition.current.x));
      cursorPosition.current.y = Math.max(-1.5, Math.min(1.5, cursorPosition.current.y));

      // Decay inertia
      touchInertia.current.x *= inertiaDecay;
      touchInertia.current.y *= inertiaDecay;

      // Stop inertia when it gets very small
      if (Math.abs(touchInertia.current.x) < 0.001) touchInertia.current.x = 0;
      if (Math.abs(touchInertia.current.y) < 0.001) touchInertia.current.y = 0;
    }

    // Reset active rotation flag after a timeout of inactivity
    if (isActivelyRotating.current && !isTouching.current) {
      const inactiveTime = Date.now() - activeTouchTime.current;
      // Reset after 1 second of inactivity
      if (inactiveTime > 1000) {
        isActivelyRotating.current = false;
      }
    }

    // Handle exit placement transition
    if (exitPlacementTransition.current.active) {
      const elapsed = (Date.now() - exitPlacementTransition.current.startTime) / 1000;
      const progress = Math.min(elapsed / exitPlacementTransition.current.duration, 1);

      if (progress >= 1) {
        // Transition complete
        exitPlacementTransition.current.active = false;
        exitPlacementTransition.current.progress = 0;
      } else {
        // Store progress for use in camera positioning
        exitPlacementTransition.current.progress = progress;

        // Apply cubic easing
        let easedProgress;
        if (progress < 0.5) {
          // Ease in: t^3 * 4
          easedProgress = 4 * Math.pow(progress, 3);
        } else {
          // Ease out: 1 - (1-t)^3 * 4
          const invProgress = 1 - progress;
          easedProgress = 1 - 4 * Math.pow(invProgress, 3);
        }

        // Smoothly transition from frozen position to current cursor position
        const lerpedX = THREE.MathUtils.lerp(
          exitPlacementTransition.current.startPosition.x,
          cursorPosition.current.x,
          easedProgress
        );
        const lerpedY = THREE.MathUtils.lerp(
          exitPlacementTransition.current.startPosition.y,
          cursorPosition.current.y,
          easedProgress
        );

        // Update a temporary cursor position for smooth transition
        exitPlacementTransition.current.currentPosition = new THREE.Vector2(lerpedX, lerpedY);
      }
    }

    // No longer need lookAtSticker transition handling since camera is never frozen

    // Cubic easing transition between modes - now based on lightMode
    if (transitionDirection.current !== 0) {
      // Calculate progress (0 to 1)
      const elapsed = t - transitionStartTime.current;
      const progress = Math.min(elapsed / transitionDuration, 1);

      if (progress >= 1) {
        // Transition complete
        transitionFactor.current = transitionDirection.current > 0 ? 1 : 0;
        transitionDirection.current = 0; // Stop transition
      } else {
        // Apply cubic easing (smooth start and end)
        let easedProgress;
        if (progress < 0.5) {
          // Ease in: t^3 * 4
          easedProgress = 4 * Math.pow(progress, 3);
        } else {
          // Ease out: 1 - (1-t)^3 * 4
          const invProgress = 1 - progress;
          easedProgress = 1 - 4 * Math.pow(invProgress, 3);
        }

        // Set transition factor based on direction
        if (transitionDirection.current > 0) {
          // Going to lights-on mode (0 to 1)
          transitionFactor.current = easedProgress;
        } else {
          // Going to lights-off mode (1 to 0)
          transitionFactor.current = 1 - easedProgress;
        }
      }
    } else if (lightMode && transitionFactor.current < 1) {
      // Start transition to lights-on mode
      transitionStartTime.current = t;
      transitionDirection.current = 1;
    } else if (!lightMode && transitionFactor.current > 0) {
      // Start transition to lights-off mode
      transitionStartTime.current = t;
      transitionDirection.current = -1;
    }

    // Calculate normal mode camera animation
    const subtleFactor = 0.05;
    const normalModeRotation = {
      x: -0.5 + Math.sin(t * 0.2) * subtleFactor,
      y: Math.sin(t * 0.1) * subtleFactor,
      z: Math.cos(t * 0.13) * subtleFactor,
    };

    // Handle camera rotation with smooth transitions between modes
    if (camera) {
      if (transitionFactor.current > 0) {
        // We're either in lights-on mode or transitioning
        // Calculate lights-on mode target rotation
        const xRotationMax = 0.3; // Maximum pitch (up/down) rotation
        const yRotationMax = 0.8; // Maximum yaw (left/right) rotation

        // Add subtle breathing movement
        const breathingX = Math.sin(t * 0.4) * 0.01;
        const breathingY = Math.cos(t * 0.3) * 0.005;

        // Use appropriate cursor position based on state
        let activeCursorPosition;

        if (isPlacing) {
          // Use frozen cursor position when placing, with smoothing to prevent jerking
          // Apply subtle damping to prevent any jerking motion when switching to/from placement mode
          if (
            Math.abs(cursorPosition.current.x - frozenCursorPosition.current.x) > 0.001 ||
            Math.abs(cursorPosition.current.y - frozenCursorPosition.current.y) > 0.001
          ) {
            // If there's significant movement, dampen it heavily to prevent jerking
            cursorPosition.current.x = THREE.MathUtils.lerp(
              cursorPosition.current.x,
              frozenCursorPosition.current.x,
              0.5 // Strong pull back to frozen position
            );
            cursorPosition.current.y = THREE.MathUtils.lerp(
              cursorPosition.current.y,
              frozenCursorPosition.current.y,
              0.5 // Strong pull back to frozen position
            );
          }
          activeCursorPosition = frozenCursorPosition.current;
        } else if (exitPlacementTransition.current.active) {
          // When transitioning out of placement, use the interpolated position
          activeCursorPosition = exitPlacementTransition.current.currentPosition;
        } else {
          // Normal operation, use current cursor position
          activeCursorPosition = cursorPosition.current;
        }

        // Calculate target rotation for lights-on mode
        const lightsModeTarget = {
          x: -0.5 + activeCursorPosition.y * xRotationMax + breathingX,
          y: -activeCursorPosition.x * yRotationMax + breathingY,
          z: Math.sin(t * 0.2) * 0.01,
        };

        // Calculate interpolated target based on transition factor
        targetCameraRotation.current.x = THREE.MathUtils.lerp(
          normalModeRotation.x,
          lightsModeTarget.x,
          transitionFactor.current
        );

        targetCameraRotation.current.y = THREE.MathUtils.lerp(
          normalModeRotation.y,
          lightsModeTarget.y,
          transitionFactor.current
        );

        // Add subtle damping effect for natural movement
        const distanceFromTarget = {
          x: Math.abs(currentCameraRotation.current.x - targetCameraRotation.current.x),
          y: Math.abs(currentCameraRotation.current.y - targetCameraRotation.current.y),
        };

        // Adjust lerp factor based on distance
        const baseLerpFactor = 0.05;
        const distanceMultiplier = 1.5;

        const xLerpFactor = baseLerpFactor * (1 + distanceFromTarget.x * distanceMultiplier);
        const yLerpFactor = baseLerpFactor * (1 + distanceFromTarget.y * distanceMultiplier);

        // Clamp lerp factors
        const clampedXLerp = Math.min(0.25, Math.max(0.01, xLerpFactor));
        const clampedYLerp = Math.min(0.25, Math.max(0.01, yLerpFactor));

        // Smoothly update current rotation
        currentCameraRotation.current.x = THREE.MathUtils.lerp(
          currentCameraRotation.current.x,
          targetCameraRotation.current.x,
          clampedXLerp
        );

        currentCameraRotation.current.y = THREE.MathUtils.lerp(
          currentCameraRotation.current.y,
          targetCameraRotation.current.y,
          clampedYLerp
        );

        // Apply rotation to camera
        camera.rotation.x = currentCameraRotation.current.x;
        camera.rotation.y = currentCameraRotation.current.y;

        // Handle z-rotation with simpler transition
        camera.rotation.z = THREE.MathUtils.lerp(
          normalModeRotation.z,
          lightsModeTarget.z,
          transitionFactor.current
        );
      } else {
        // Fully in normal mode, just use the normal animation
        camera.rotation.x = normalModeRotation.x;
        camera.rotation.y = normalModeRotation.y;
        camera.rotation.z = normalModeRotation.z;

        // Reset tracking variables
        currentCameraRotation.current.set(-0.5, 0, 0);
        targetCameraRotation.current.set(-0.5, 0, 0);
      }
    }

    // Transition light intensity based on light mode
    if (pointLightRef.current) {
      if (lightMode) {
        pointLightRef.current.intensity = THREE.MathUtils.lerp(
          pointLightRef.current.intensity,
          2.0, // Target intensity
          0.02 // Transition speed
        );
      } else {
        pointLightRef.current.intensity = THREE.MathUtils.lerp(
          pointLightRef.current.intensity,
          0.5, // Original intensity
          0.02 // Transition speed
        );
      }
    }

    // Update preview sticker position - use frozen intersection if in placing mode
    if (stickerMode && previewSticker) {
      const currentIntersection = isPlacing ? frozenIntersection : intersection;

      if (currentIntersection) {
        // Apply offset to prevent z-fighting
        // Use a slightly larger offset for preview to ensure it's above all placed stickers
        const previewZOffset = 0.06 + placedStickers.length * 0.0005;
        const offsetPosition = currentIntersection.point
          .clone()
          .add(currentIntersection.normal.clone().multiplyScalar(previewZOffset));

        // Use the offset position
        previewSticker.position.copy(offsetPosition);

        // Create a rotation matrix from the normal
        const rotationMatrix = new THREE.Matrix4();
        const normalVector = currentIntersection.normal;

        // Create a target vector that is perpendicular to the normal
        const up = new THREE.Vector3(0, 1, 0);
        const right = new THREE.Vector3().crossVectors(up, normalVector).normalize();
        const newUp = new THREE.Vector3().crossVectors(normalVector, right).normalize();

        // Set the rotation matrix
        rotationMatrix.makeBasis(right, newUp, normalVector);
        const baseQuaternion = new THREE.Quaternion().setFromRotationMatrix(rotationMatrix);

        // Apply base rotation to the preview sticker
        previewSticker.quaternion.copy(baseQuaternion);

        // Use frozen rotation and scale values if in placing mode
        const currentRotationAngle = isPlacing ? frozenRotationAngle : randomRotationAngle;
        const currentScale = isPlacing ? frozenScale : randomScale;

        // Create a rotation around the normal vector only
        const normalRotation = new THREE.Quaternion().setFromAxisAngle(
          normalVector,
          currentRotationAngle
        );
        previewSticker.quaternion.premultiply(normalRotation);

        // Apply scale
        previewSticker.scale.set(currentScale, currentScale, currentScale);
      }
    }

    // Reset the flag after potentially loading new textures
    shouldUpdateStickers.current = false;
  });

  // Create preview sticker when we have an intersection
  useEffect(() => {
    // Only create new preview sticker if not currently placing and active placement is ready
    if (stickerMode && !isPlacing && intersection && stickerImage) {
      // Only show preview if we're actively trying to place a sticker
      if (!userUploadedStickerImage) {
        // No active placement in progress, don't show preview
        setPreviewSticker(null);
        return;
      }

      const material = new THREE.MeshStandardMaterial({
        map: stickerImage,
        transparent: true,
        side: THREE.DoubleSide,
        roughness: 0.5,
        metalness: 0.0,
        alphaTest: 0.1, // Add alpha test to improve transparency
        depthWrite: false, // Disable depth writing for better transparency
        depthTest: true, // Keep depth test enabled but will be overridden by renderOrder
      });

      // Calculate width and height based on aspect ratio while maintaining area
      const width = stickerSize * Math.sqrt(stickersAspectRatio);
      const height = stickerSize / Math.sqrt(stickersAspectRatio);

      const geometry = new THREE.PlaneGeometry(width, height);
      const mesh = new THREE.Mesh(geometry, material);

      // Adjust the position with a larger offset to prevent z-fighting
      const previewZOffset = 0.06 + placedStickers.length * 0.0005;
      const offsetPosition = intersection.point
        .clone()
        .add(intersection.normal.clone().multiplyScalar(previewZOffset));

      mesh.position.copy(offsetPosition);

      // Set the rotation based on normal
      const rotationMatrix = new THREE.Matrix4();
      const normalVector = intersection.normal;

      // Create a target vector that is perpendicular to the normal
      const up = new THREE.Vector3(0, 1, 0);
      const right = new THREE.Vector3().crossVectors(up, normalVector).normalize();
      const newUp = new THREE.Vector3().crossVectors(normalVector, right).normalize();

      // Set the rotation matrix
      rotationMatrix.makeBasis(right, newUp, normalVector);
      const quaternion = new THREE.Quaternion().setFromRotationMatrix(rotationMatrix);

      mesh.quaternion.copy(quaternion);

      // Apply rotation around the normal vector only
      const normalRotation = new THREE.Quaternion().setFromAxisAngle(
        normalVector,
        randomRotationAngle
      );
      mesh.quaternion.premultiply(normalRotation);

      // Apply random scale
      mesh.scale.set(randomScale, randomScale, randomScale);

      // Set a high renderOrder value to make sure preview renders on top of placed stickers
      // Use a value between placed stickers (1000+index) and pending stickers (5000+index)
      mesh.renderOrder = 10000;

      setPreviewSticker(mesh);
    } else if (!stickerMode || !userUploadedStickerImage) {
      // If not in sticker mode or no active sticker placement, ensure preview is cleared
      setPreviewSticker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    intersection,
    stickerMode,
    stickerImage,
    randomRotationAngle,
    randomScale,
    isPlacing,
    userUploadedStickerImage,
  ]);

  // Notify parent when isPlacing changes
  useEffect(() => {
    if (onPlacingChange) {
      onPlacingChange(isPlacing);
    }
  }, [isPlacing, onPlacingChange]);

  return (
    <>
      {/* Add a bright test light */}
      <pointLight ref={pointLightRef} position={[0, 2, 0]} intensity={0.5} color="white" />

      {/* Ambient blue-tinted light */}
      <ambientLight color={blueLight} intensity={ambientIntensity} />

      {/* Directional light from above - dim blue */}
      <directionalLight position={[0, 5, -5]} color={blueLight} intensity={directionalIntensity} />

      {/* Bathroom stall model */}
      <group ref={stall}>
        {/* Bathroom stalls model */}
        <mesh rotation={[0, (2 * -Math.PI) / 2, 0]} position={[0.18, 0, -1.2]}>
          <primitive object={useGLTF('/bathroom.glb').scene} />
          <meshStandardMaterial color="#FFF" roughness={0.8} />
        </mesh>
        {/* Floor */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          <planeGeometry args={[10, 10]} />
          <meshStandardMaterial
            map={floorTexture}
            roughness={0}
            metalness={0.9}
            envMapIntensity={10}
            normalScale={[10.3, 10.3]}
            normalMap={floorTexture}
            aoMap={floorTexture}
            aoMapIntensity={1}
            roughnessMap={floorTexture}
            metalnessMap={floorTexture}
          />
        </mesh>
      </group>

      {/* Fog for atmosphere */}
      <fog attach="fog" args={[blueLight, 1, 15]} />

      {/* Placed stickers - render these FIRST (before pending stickers) */}
      {placedStickers
        // Sort stickers by timestamp to ensure consistent z-ordering (oldest first, newest last)
        .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
        .map((sticker, index) => {
          // Create position and normal vectors
          const position = new THREE.Vector3(
            sticker.position.x,
            sticker.position.y,
            sticker.position.z
          );

          const normal = new THREE.Vector3(sticker.normal.x, sticker.normal.y, sticker.normal.z);

          // Create rotation from normal
          const rotationMatrix = new THREE.Matrix4();
          const up = new THREE.Vector3(0, 1, 0);
          const right = new THREE.Vector3().crossVectors(up, normal).normalize();
          const newUp = new THREE.Vector3().crossVectors(normal, right).normalize();

          // Set the rotation matrix and get quaternion
          rotationMatrix.makeBasis(right, newUp, normal);
          const quaternion = new THREE.Quaternion().setFromRotationMatrix(rotationMatrix);

          // Apply the saved rotation angle around the normal
          const rotationAngle = sticker.rotationAngle !== undefined ? sticker.rotationAngle : 0;
          const normalRotation = new THREE.Quaternion().setFromAxisAngle(normal, rotationAngle);
          quaternion.premultiply(normalRotation);

          // Get the scale value from the stored sticker data or use default
          const scale = sticker.scale !== undefined ? sticker.scale : 1;

          // Get the correct texture for this sticker
          const textureToUse = customStickerTexturesRef.current[index];

          // Skip rendering if no texture is available yet
          if (!textureToUse) return null;

          // Calculate aspect ratio directly from texture
          let stickerAspectRatio = 1; // Default to square
          if (textureToUse && textureToUse.image) {
            stickerAspectRatio = textureToUse.image.width / textureToUse.image.height;
          }

          // Calculate width and height based on aspect ratio while maintaining area
          const width = stickerSize * Math.sqrt(stickerAspectRatio);
          const height = stickerSize / Math.sqrt(stickerAspectRatio);

          // Create a larger offset based on index to prevent z-fighting
          // The offset is applied in the direction of the normal
          const zFightingOffset = 0.002; // Increased for better separation
          const indexOffset = index * zFightingOffset;
          const positionWithOffset = position
            .clone()
            .add(normal.clone().multiplyScalar(indexOffset));

          return (
            <mesh
              key={`sticker-${index}-${sticker.timestamp || Date.now()}`} // Stable key with fallback timestamp
              position={positionWithOffset}
              quaternion={quaternion}
              scale={[scale, scale, scale]}
              renderOrder={1000 + index} // Lower render order for placed stickers
            >
              <planeGeometry args={[width, height]} />
              <meshStandardMaterial
                map={textureToUse}
                transparent={true}
                side={THREE.DoubleSide}
                roughness={0.5}
                metalness={0.0}
                alphaTest={0.1}
                depthWrite={false}
                depthTest={true}
              />
            </mesh>
          );
        })}

      {/* Preview sticker - render after placed stickers but before pending */}
      {previewSticker && <primitive object={previewSticker} renderOrder={10000} />}

      {/* Pending stickers - render these LAST (after placed stickers) */}
      {stickerImage &&
        pendingStickers.map((sticker, index) => {
          // Create position and normal vectors
          const position = new THREE.Vector3(
            sticker.position.x,
            sticker.position.y,
            sticker.position.z
          );

          const normal = new THREE.Vector3(sticker.normal.x, sticker.normal.y, sticker.normal.z);

          // Create rotation from normal
          const rotationMatrix = new THREE.Matrix4();
          const up = new THREE.Vector3(0, 1, 0);
          const right = new THREE.Vector3().crossVectors(up, normal).normalize();
          const newUp = new THREE.Vector3().crossVectors(normal, right).normalize();

          // Set the rotation matrix and get quaternion
          rotationMatrix.makeBasis(right, newUp, normal);
          const quaternion = new THREE.Quaternion().setFromRotationMatrix(rotationMatrix);

          // Apply the saved rotation angle around the normal
          const rotationAngle = sticker.rotationAngle !== undefined ? sticker.rotationAngle : 0;
          const normalRotation = new THREE.Quaternion().setFromAxisAngle(normal, rotationAngle);
          quaternion.premultiply(normalRotation);

          // Get the scale value from the stored sticker data or use default
          const scale = sticker.scale !== undefined ? sticker.scale : 1;

          // Create texture for this sticker
          let textureToUse = stickerImage; // Default fallback

          // Check if we already created a texture for this pending sticker
          const pendingKey = `pending-${index}`;

          // Get aspect ratio - for pending stickers, use the current sticker being placed
          let stickerAspectRatio = stickersAspectRatio || 1;

          // Use cached texture if available
          if (customStickerTexturesRef.current[pendingKey]) {
            textureToUse = customStickerTexturesRef.current[pendingKey];
            // Attempt to get aspect ratio from texture if available
            if (textureToUse.image) {
              stickerAspectRatio = textureToUse.image.width / textureToUse.image.height;
            }
          }

          // Calculate width and height based on aspect ratio while maintaining area
          const width = stickerSize * Math.sqrt(stickerAspectRatio);
          const height = stickerSize / Math.sqrt(stickerAspectRatio);

          // Position already has offset since we modified it in handleClick

          // Much larger forward offset and z-fighting prevention
          const zFightingOffset = 0.003; // Larger offset for pending stickers
          const indexOffset = index * zFightingOffset;
          const pendingBaseOffset = 0.02; // Add 0.02 units forward to all pending stickers
          const positionWithOffset = position
            .clone()
            .add(normal.clone().multiplyScalar(pendingBaseOffset + indexOffset));

          // Only create a custom texture if imageData is provided and not already cached
          if (sticker.imageData && !customStickerTexturesRef.current[pendingKey]) {
            const img = new Image();
            img.src = sticker.imageData;
            const texture = new THREE.Texture(img);
            img.onload = () => {
              texture.needsUpdate = true;
              texture.premultiplyAlpha = false; // Fix transparency handling
              customStickerTexturesRef.current[pendingKey] = texture;
              // Force re-render when image loads to update aspect ratio
              shouldUpdateStickers.current = true;
            };
            img.onerror = () => {
              // eslint-disable-next-line no-console
              console.error(`Failed to load pending sticker image, using fallback`);
              // Just use the fallback texture (stickerImage) which is already set
            };
            customStickerTexturesRef.current[pendingKey] = texture;
          }

          return (
            <mesh
              key={`pending-sticker-${index}-${sticker.timestamp || index}`}
              position={positionWithOffset}
              quaternion={quaternion}
              scale={[scale, scale, scale]}
              renderOrder={5000 + index} // Much higher render order for pending stickers
            >
              <planeGeometry args={[width, height]} />
              <meshStandardMaterial
                map={textureToUse}
                transparent={true}
                side={THREE.DoubleSide}
                roughness={0.5}
                metalness={0.0}
                alphaTest={0.1}
                depthWrite={false}
                depthTest={false} // Disable depth testing completely for pending stickers
              />
            </mesh>
          );
        })}
    </>
  );
}
